// What the editor's forms (forms/*.vue, opened by EditDrawer) share: the
// node and its config, the machine's GPUs and addresses, DeckLink ports
// still free, the media library, and the port hints of the listener forms.
import { computed, onMounted, ref, type ComputedRef } from 'vue'
import { api, type EditTarget, type Media } from '../../api'
import { useNode } from '../../store'
import { freePort, portsInUse } from '../../util'

// structuredClone cannot copy Vue's reactive proxies; a JSON round trip can
export const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T

export { ipKinds, newInputFormat } from '../../inputformat'

export function useEditContext(t: EditTarget) {
    const node = useNode()
    const cfg = computed(() => node.config!)
    return {
        node,
        cfg,
        // a player node: IP inputs shown on an HDMI screen, no capture or encodes
        player: computed(() => cfg.value.mode === 'player'),
        // the node's IPv4 addresses, for the multicast interface selects
        ipv4: computed(() => (node.status?.machine.addresses ?? []).filter((a) => /^\d+\.\d+\.\d+\.\d+$/.test(a))),
        // GPUs the node reports (nvidia-smi), for the NVENC and NVDEC GPU selects
        gpus: computed(() => node.status?.machine.gpus ?? []),
        isNew: computed(() => t.kind !== 'node' && !t.id),
    }
}

// the media library: media inputs, fallback and overlay pictures, an
// encode's picture on signal loss
export function useMedia() {
    const media = ref<Media[]>([])
    onMounted(async () => {
        try {
            media.value = await api.media()
        } catch {
            /* offline */
        }
    })
    return media
}

// A DeckLink connector is exclusive: the ones no other input or output
// uses (self: the entity being edited, whose own port stays offered).
export function useDeckLink(self: () => { kind: 'input' | 'output'; id: string }) {
    const node = useNode()
    const taken = computed(() => {
        const s = self()
        const used = new Set<string>()
        for (const i of node.config?.inputs ?? [])
            if (i.kind === 'decklink' && i.device && !(s.kind === 'input' && i.id === s.id)) used.add(i.device)
        for (const o of node.config?.outputs ?? [])
            if (o.kind === 'decklink' && o.device && !(s.kind === 'output' && o.id === s.id)) used.add(o.device)
        return used
    })
    const ports = computed(() => node.status?.machine.decklink ?? [])
    return {
        taken,
        decklinkIn: computed(() => ports.value.filter((d) => d.input && !taken.value.has(d.name))),
        decklinkOut: computed(() => ports.value.filter((d) => d.output && !taken.value.has(d.name))),
    }
}

// Ports: what this form would listen on (mine), who else listens on this
// node, and the line under the port field.
export function usePorts(t: EditTarget, mine: ComputedRef<{ proto: 'udp' | 'tcp'; port: number } | null>) {
    const node = useNode()
    const pullDef = computed(() => node.status?.pull_ports ?? { rtmp: 1935, srt: 8890, hls: 8888 })
    const otherPorts = computed(() =>
        portsInUse(node.config!, pullDef.value).filter((u) => u.key !== `${t.kind}:${'id' in t ? t.id : ''}`),
    )
    const portClash = computed(
        () => mine.value && otherPorts.value.find((u) => u.proto === mine.value!.proto && u.port === mine.value!.port),
    )
    const portNote = computed(() => {
        const m = mine.value
        if (!m) return ''
        if (portClash.value)
            return `Port ${m.port}/${m.proto} is already used by ${portClash.value.what}; next free: ${freePort(otherPorts.value, m.proto, m.port)}.`
        const list = otherPorts.value
            .filter((u) => u.proto === m.proto)
            .map((u) => `${u.port} ${u.what}`)
            .join(' · ')
        return list
            ? `${m.proto.toUpperCase()} ports in use on this node: ${list}`
            : `No other ${m.proto.toUpperCase()} listeners on this node.`
    })
    return { pullDef, otherPorts, portClash, portNote }
}

// "a, b c" -> ["a", "b", "c"]
export const list = (s: string) =>
    s
        .split(/[,\s]+/)
        .map((x) => x.trim())
        .filter(Boolean)
